<template>
  <DSDialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DSDialogTrigger
      v-if="$slots.default"
      as-child
    >
      <slot />
    </DSDialogTrigger>

    <DSDialogContent class="max-w-ds-dialog-xl gap-0">
      <DSDialogHeader class="border-b border-border">
        <DSDialogTitle class="text-xl">
          Add assets
        </DSDialogTitle>
        <!--
          Reka requires a description for the dialog's aria-describedby. The
          design has no subtitle here — the two section headings carry the
          meaning — so it is available to screen readers only.
        -->
        <DSDialogDescription class="sr-only">
          Connect an institution to sync an account, or describe in your own words
          what happened and let the wizard fill in the rest.
        </DSDialogDescription>
      </DSDialogHeader>

      <AddAssetConnect
        :disabled="connecting"
        @connect="emit('connect', $event)"
        @more-options="emit('more-options')"
      />

      <AddAssetCompose
        @describe="emit('describe', $event)"
        @pick-flow="emit('pick-flow', $event)"
      />
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import {
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogHeader,
  DSDialogTitle,
  DSDialogTrigger,
} from '@ds'

import { InstatutionData } from '@/views/LinkedData/utils/types'
import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import AddAssetCompose from './components/AddAssetCompose.vue'
import AddAssetConnect from './components/AddAssetConnect.vue'

defineProps<{
  /**
   * Controlled open state. Deliberately left without a default: passing
   * `false` rather than `undefined` would put Reka's DialogRoot into
   * controlled mode permanently, and the trigger-driven form would stop
   * opening.
   */
  open?: boolean
  /** Set while a connect handshake is in flight. */
  connecting?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', open: boolean): void
  /** An institution tile was chosen — start its connect flow. */
  (e: 'connect', institution: InstatutionData): void
  /** The full institution browser was requested. */
  (e: 'more-options'): void
  /** The user described an asset in their own words. */
  (e: 'describe', sentence: string): void
  (e: 'pick-flow', flow: AssetWizardFlow): void
}>()
</script>
